import React, { useCallback, useEffect, useRef, useState } from 'react'

import classnames from 'classnames'
import PropTypes from 'prop-types'
import uniqid from 'uniqid'

import { Spinner } from '../Spinner/Spinner'
import './Select.scss'
import { SelectSelected } from './SelectSelected'
import { SelectTray } from './SelectTray'

import useHandleClickOutside from '../../hooks/useHandleClickOutside'

export const OPTION_TYPE_SELECTABLE = 'OPTION_TYPE_SELECTABLE'
export const OPTION_TYPE_PLACEHOLDER = 'OPTION_TYPE_PLACEHOLDER'

export const SELECT_OPEN_BOTTOM = true // true = apre sotto
export const SELECT_OPEN_TOP = false // false = apre sopra
const SELECT_VARIANT_DROPDOWN = 'dropdown'
const SELECT_VARIANT_INLINE = 'inline'
export const SELECT_VARIANT_WITH_CHECKBOX = 'with-checkbox'

const uuid = uniqid()

export const Select = ({
  className,
  disabled,
  error,
  hint,
  hintNoResults,
  inputName,
  loading,
  multiple,
  onChange,
  onSearch,
  onSubmit,
  onBlur,
  optionGroups,
  options,
  placeholder,
  searchable,
  SelectedItemComponent,
  OptionComponent,
  value,
  variant,
  onExpandCollapse,
  forceCollapse,
  'data-mu-test': dataAttributeTest,
  memoSelectOption,
  forceOpenUp
}) => {
  const [open, setOpen] = useState(false)
  const [focus, setFocus] = useState(false)
  const [searchString, setSearchString] = useState('')
  const selectRef = useRef(null)

  const selectedOptions = multiple
    ? value
        .map(value => options.find(option => option.value === value))
        .filter(o => !!o)
    : options.find(option => value === option.value)

  const optionsFiltered = options
    .filter(option => !option.hidden)
    .filter(option => {
      const escapedSearchString = searchString.replace(
        /[-[\]{}()*+?.,\\^$|#\s]/g,
        '\\$&'
      )
      return new RegExp(escapedSearchString, 'i').test(option.label)
    })
    // .filter(option => ![].concat(value).includes(option.value))
    .map(option => ({
      ...option,
      type: OPTION_TYPE_SELECTABLE,
      selected: [].concat(value).includes(option.value)
    }))

  const optionPlaceholder = {
    label: placeholder,
    value: undefined,
    type: OPTION_TYPE_PLACEHOLDER
  }

  const optionsVisible = []
    .concat(placeholder ? optionPlaceholder : [])
    .concat(optionsFiltered)

  const searchMatches =
    optionsFiltered.length === 1 && optionsFiltered[0].label === searchString

  const optionFocused = useRef(null)

  const elInput = useRef()

  const expand = useCallback(() => {
    setFocus(true)
    elInput.current.focus()
    setOpen(true)
  }, [])

  const collapse = useCallback(() => {
    setFocus(false)
    setSearchString('')
    elInput && elInput.current && elInput.current.blur()
    setOpen(false)
  }, [])

  useHandleClickOutside(selectRef, collapse)

  useEffect(() => {
    typeof onExpandCollapse === 'function' && onExpandCollapse(open)
  }, [open])

  const onClickDocument = useCallback(() => {
    collapse()
  }, [])

  const toggle = useCallback(
    e => {
      e.nativeEvent.stopImmediatePropagation()

      if (open) {
        collapse()
        return
      }
      expand()
    },
    [open]
  )

  const handleSelectOption = useCallback(
    option => {
      if (!(multiple && variant === SELECT_VARIANT_WITH_CHECKBOX)) {
        setSearchString('')
      }

      if (multiple) {
        if (option.value === undefined) {
          return
        }

        if (option.selected) {
          if (variant === SELECT_VARIANT_WITH_CHECKBOX) {
            onChange([...(value || [])].filter(value => value !== option.value))
          }
          return
        }

        onChange([...(value || []), option.value])
        return
      }
      onChange(option.value)
    },
    [value, multiple]
  )

  const removeValue = useCallback(
    valueToRemove => {
      !disabled && onChange(value.filter(value => value !== valueToRemove))
    },
    [value, disabled]
  )

  const handleChangeSearch = useCallback(
    e => {
      if (!searchable) {
        return
      }
      if (typeof onSearch === 'function') {
        const searchString = onSearch(e.target.value) || ''
        setSearchString(searchString)
        return
      }

      setSearchString(e.target.value)
    },
    [searchable, onSearch]
  )

  const onKeyDown = useCallback(
    e => {
      expand()
      if (e.keyCode === 8) {
        // backspace
        if (multiple && focus && selectedOptions.length && !e.target.value) {
          removeValue(selectedOptions[selectedOptions.length - 1].value)
        }
      }
      if (e.keyCode === 13) {
        // enter
        if (open) {
          e.stopPropagation()
          e.preventDefault()
        }
        if (
          optionFocused.current &&
          optionFocused.current.type === OPTION_TYPE_SELECTABLE
        ) {
          handleSelectOption(optionFocused.current)
          return
        }
        if (!!searchString && !searchMatches) {
          typeof onSubmit === 'function' && onSubmit(searchString)
          setSearchString('')
        }
      }
    },
    [selectedOptions, searchString, optionFocused.current]
  )

  useEffect(() => {
    elInput.current.addEventListener('focus', () => {
      setFocus(true)
    })

    const clear = () => {
      window.removeEventListener('click', onClickDocument)
    }
    return clear
  }, [])

  useEffect(() => {
    if (forceCollapse) collapse()
  }, [forceCollapse])

  // SMART SELECT
  const [openBottom, setOpenDirection] = useState(SELECT_OPEN_BOTTOM)

  const getAvailableSpace = () => {
    if (selectRef.current) {
      const elemRect = selectRef.current.getBoundingClientRect() // get reference to element
      const breakpointHeight = parseInt(window.innerHeight - 245) // calcola 240px (max-height della selectTray) dal fondo
      const distanceFromElementBottom = elemRect.bottom // distanza tra fondo schermo e bottom dell'ellemento

      const isAboveBreakpoint = breakpointHeight > distanceFromElementBottom
      const isBelowBreakpoint = breakpointHeight <= distanceFromElementBottom

      const isInViewport =
        elemRect.top >= 0 &&
        elemRect.left >= 0 &&
        elemRect.bottom <= window.innerHeight &&
        elemRect.right <= window.innerWidth

      // se nella viewport && nella metà alta del breakpoint => apri verso il basso
      // se nella viewport && nella mmetà bassa del breakpoint => apri verso l'alto
      if (isInViewport) {
        if (isAboveBreakpoint) {
          setOpenDirection(SELECT_OPEN_BOTTOM)
        } else if (isBelowBreakpoint) {
          setOpenDirection(SELECT_OPEN_TOP)
        }
      }
    }
  }

  useEffect(() => {
    getAvailableSpace()

    window.addEventListener('scroll', getAvailableSpace)
    return () => window.removeEventListener('scroll', getAvailableSpace)
  }, [])

  return (
    <div
      ref={selectRef}
      className={classnames(className, 'e-select', {
        'e-select--focus': focus,
        'e-select--expanded': open,
        'e-select--disabled': disabled,
        'e-select--searchable': searchable,
        'e-select--filtered': !!searchString,
        'e-select--multiple': multiple,
        'e-select--has-value': [].concat(value).length,
        'e-select--loading': loading,
        'e-select--danger': error,
        'e-select--inline': variant === SELECT_VARIANT_INLINE
      })}
      onClick={disabled ? undefined : toggle}
      data-mu-test={classnames('select', dataAttributeTest)}
    >
      <div
        className={classnames('e-select__search', {
          'e-select__search--multiple': multiple
        })}
      >
        {searchable && <span className='e-select__search__icon' />}
        <SelectSelected
          placeholder={placeholder}
          selection={selectedOptions}
          onRemove={removeValue}
          SelectedItemComponent={SelectedItemComponent}
          disabled={disabled}
        />
        <input
          className='e-select__search__input'
          type='text'
          value={searchString}
          onChange={searchable ? handleChangeSearch : undefined}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={
            disabled || !searchable || variant === SELECT_VARIANT_INLINE
          }
          ref={elInput}
          autoComplete='off'
        />
        {loading && (
          <span className='e-select__search__spinner'>
            <Spinner />
          </span>
        )}
      </div>
      <div
        className={
          open && searchable && focus // || (disabled && multiple)
            ? 'e-select__no-control'
            : 'e-select__control'
        }
      />
      {open && (
        <SelectTray
          OptionComponent={OptionComponent}
          searchString={searchString}
          options={optionsVisible}
          optionGroups={optionGroups}
          onSelect={handleSelectOption}
          hint={hint}
          hintNoResults={hintNoResults}
          collapse={collapse}
          multiple={multiple}
          loading={loading}
          preselect={onSubmit ? searchMatches : optionsFiltered.length === 1}
          onChangeFocus={item => {
            optionFocused.current = item
          }}
          withCheckbox={variant === SELECT_VARIANT_WITH_CHECKBOX}
          placeholder={placeholder}
          openBottom={forceOpenUp ? false : openBottom}
          memoSelectOption={memoSelectOption}
        />
      )}

      <select
        className='e-select__select'
        name={inputName}
        value={value}
        multiple={multiple}
        readOnly
      >
        {options.map((option, i) => (
          <option key={`e-select-option-${i}-${uuid}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

Select.defaultProps = {
  variant: SELECT_VARIANT_DROPDOWN,
  options: [],
  searchable: true,
  forceCollapse: false,
  SelectedItemComponent: undefined,
  memoSelectOption: false,
  forceOpenUp: false
}

Select.propTypes = {
  /**
   * Text to show when no item is selected
   */
  placeholder: PropTypes.string,
  /**
   * Additional classnames
   */
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /**
   * Graphic variant. Possible values `dropdown` (default), `inline`
   */
  variant: PropTypes.string,
  /**
   * The name of the html &lt;select&gt; element
   */
  inputName: PropTypes.string,
  /**
   * Selected value(s)
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ]),
  /**
   * An array of options
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      disabled: PropTypes.bool,
      hidden: PropTypes.bool
    })
  ),
  optionGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      )
    })
  ),
  /**
   * Fires on the selection of an option
   */
  onChange: PropTypes.func,
  /**
   * A callback triggered when something is typed into field. Side effects such
   * as the update of option set should be managed here before returning the string value
   * @param {string} value the text field value
   * @returns {string|boolean} value to update search string state.
   */
  onSearch: PropTypes.func,
  /**
   * A callback triggered when enter key is pressed and no available options are selected. Side effects such
   * as the update of option set should be managed here before returning the string value
   * @param {string} value the text field value
   */
  onSubmit: PropTypes.func,
  onBlur: PropTypes.func,
  /**
   * Whether or not the search is enabled
   */
  searchable: PropTypes.bool,
  /**
   * *NOT RECOMMENDED.* Wanna override <Chip /> content with something fancy? Yu got served.
   */
  SelectedItemComponent: PropTypes.any,

  /**
   * Custom component for rendering option element in select tray
   */
  OptionComponent: PropTypes.elementType,

  /**
   * Allow multiple selection
   */
  multiple: PropTypes.bool,
  /**
   * Additional message to be shown at the end of the select list
   */
  hint: PropTypes.string,
  /**
   * Additional message to be shown when Searchable and there are no results
   */
  hintNoResults: PropTypes.string,
  /**
   * Whether or not to show the loading state
   */
  loading: PropTypes.bool,
  /**
   * Whether or not to show the error state
   */
  error: PropTypes.bool,
  /**
   * A callback triggered when Select expands or collapses
   */
  onExpandCollapse: PropTypes.func,
  /**
   * Force Select to collapse
   */
  forceCollapse: PropTypes.bool,

  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string,

  /**
   * Use memoized SelectOption component
   */
  memoSelectOption: PropTypes.bool,

  /**
   * force Select tray to open up
   */
  forceOpenUp: PropTypes.bool
}
